import type { ListPredictionsOutput } from "../../../types/generated/api/ListPredictionsOutput"
import { convertCsvToJson, readFileContentAsync } from "../../../utils/genericHelpers"
import type { WorkflowDetails } from "../interfaces"

/**
 * Derives a human-readable decision reason from a prediction output.
 * Returns label text, tooltip, and a computed type string.
 */
export const decisionReason = (
  rowData: ListPredictionsOutput,
  options: { category?: string; aiEngineLabel: string },
): { text: string; tooltip?: string; type: string } => {
  const tooltip = rowData.workflow_output?.description
  let text = ""
  let type = options.category === "workflow" ? "__" : options.aiEngineLabel

  if (rowData.workflow_output) {
    let lastNode = rowData.workflow_output?.satisfied_flow_path?.slice(-1)[0]
    if (lastNode?.type !== "project") {
      if (lastNode?.type === "loop-start") {
        const lastIteration = lastNode.iterations[Object.keys(lastNode.iterations).length]
        lastNode = lastIteration[lastIteration.length - 1]
      }
      text = lastNode ? `${String(lastNode?.type).charAt(0).toUpperCase()}${String(lastNode?.type).slice(1)}` : "__"
    }
    type = lastNode?.type
    if (rowData.workflow_output.reason) type += `: ${rowData.workflow_output.reason}`
  }

  return { text, type, tooltip }
}

/**
 * Maps backend workflow node type into a UI node type used by ReactFlow renderers.
 */
export const toNodeType = (type: string): string => {
  const map: Record<string, string> = {
    filter: "FilterNode",
    ruleset: "RulesetNode",
    program: "ProgramNode",
    calculator: "CalculatorNode",
    taglist: "TaglistNode",
    label: "LabeledNode",
    scorecardset: "ScorecardsetNode",
    script: "ScriptNode",
  }

  return map[type.toLowerCase()] ?? type
}

/**
 * Removes the satisfied_flow_path property from a workflow output object.
 * This is useful for displaying workflow output without the flow path data.
 *
 * @param output - The workflow output object that may contain satisfied_flow_path
 * @returns A new object with satisfied_flow_path removed, or empty object if input is null/undefined
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function omitSatisfiedFlowPath(output?: Record<string, any> | null): Record<string, any> {
  if (!output) return {}
  const { ...rest } = output
  delete rest.satisfied_flow_path
  return rest
}

/**
 * Configuration for workflow status tags
 */
interface WorkflowStatusTagConfig {
  color: "red" | "green" | "blue"
  translationKey: string
}

/**
 * Maps workflow status to tag configuration
 */
const WORKFLOW_STATUS_TAG_MAP: Record<string, WorkflowStatusTagConfig> = {
  deprecated: {
    color: "red",
    translationKey: "tags.deprecated",
  },
  active: {
    color: "green",
    translationKey: "tags.active",
  },
  disabled: {
    color: "blue",
    translationKey: "tags.idle",
  },
}

/**
 * Factory function to get workflow status tag configuration
 * @param workflowDetails - The workflow details object
 * @returns Tag configuration or null if no status should be displayed
 */
export function getWorkflowStatusTagConfig(workflowDetails?: WorkflowDetails | null): WorkflowStatusTagConfig | null {
  if (!workflowDetails) return null

  if (workflowDetails.is_deprecated) {
    return WORKFLOW_STATUS_TAG_MAP.deprecated ?? null
  }

  const stateKey = workflowDetails.state?.toLowerCase()
  return WORKFLOW_STATUS_TAG_MAP[stateKey] ?? null
}

/**
 * Converts file content to JSON array based on file type
 *
 * @param file - The file to convert
 * @returns Promise resolving to an array of JSON objects
 */
export async function convertFileToJson(file: File): Promise<Array<Record<string, string | null>>> {
  const fileRows = await readFileContentAsync(file)

  if (file.name.endsWith(".csv")) {
    return convertCsvToJson(fileRows)
  } else if (file.name.endsWith(".json")) {
    return JSON.parse(fileRows)
  }

  throw new Error("Unsupported file type")
}
